import {useState, useCallback, useRef} from 'react'
import {ChatInputDisplay} from './ChatInputDisplay'
import {CLIChatInputHeader} from './CLIChatInputHeader'
import {CLIModePicker} from './CLIModePicker'
import {CLIStopButton} from './CLIStopButton'
import {PromptInput} from './PromptInput'
import {useCreateCommand} from '../../hooks/useCreateCommand'
import {useCLIPromptState} from '../../hooks/use-cli-prompt-state'
import {computeConnectionStatus} from '../../utils/connection-status'
import {useCLIModeSync} from '../../hooks/use-cli-mode-sync'
import {COMMAND_TYPES} from '../../constants/command-types'
import {isActiveSession} from '../../types/session'
import type {CommandType, Session} from '../../types/session'
import type {CLIAgentMode} from './CLIModePicker'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {useOptimisticLogs} from '../../contexts/OptimisticLogsContext'
import {BlockedTaskInput} from './BlockedTaskInput'
import styles from './ChatInputDisplay.module.css'

interface CLIChatInputProps {
  /** The CLI session */
  session: Session
  /** Whether the CLI session supports remote steering. When false, shows a blocked message. */
  remoteSteerable?: boolean
  /** The agent task ID used in the CLI resume command */
  agentTaskId?: string
  /** The task-level stale_at deadline for computing connection status */
  staleAt?: string
  /** Timestamp of the last successful task query fetch (React Query dataUpdatedAt) */
  dataUpdatedAt?: number
  /** Callback when a command is successfully sent */
  onCommandSent?: () => void
  /** Whether the CLI remote session notice has been dismissed (from route query) */
  cliRemoteNoticeDismissed?: boolean
}

/**
 * Input component for CLI sessions.
 * Shows PromptInput when there's a pending prompt, otherwise shows ChatInputDisplay.
 * Handles both regular message submission and prompt response submission.
 */
export function CLIChatInput({
  session,
  remoteSteerable,
  agentTaskId,
  staleAt,
  dataUpdatedAt,
  onCommandSent,
  cliRemoteNoticeDismissed,
}: CLIChatInputProps) {
  const connectionStatus = computeConnectionStatus(staleAt, true, session.state, dataUpdatedAt)
  const sessionId = session.id
  const [value, setValue] = useState('')
  const isPromptResponseRef = useRef(false)
  const optimisticIdRef = useRef<string | null>(null)
  const {agentMode, isPending: isModePending, handleModeChange, handleModeChangeError} = useCLIModeSync({sessionId})

  const {pendingPrompt, isLoading, markPromptResolved, dismissPrompt} = useCLIPromptState({
    sessionId,
    enabled: true,
  })

  const {addOptimisticResponse, removeOptimisticResponse} = useOptimisticLogs()

  const {createCommand, isPending} = useCreateCommand({
    onSuccess: () => {
      if (isPromptResponseRef.current) {
        markPromptResolved()
        isPromptResponseRef.current = false
      }

      optimisticIdRef.current = null
    },
    onError: (_error, snapshot) => {
      isPromptResponseRef.current = false
      if (optimisticIdRef.current) {
        removeOptimisticResponse(optimisticIdRef.current)
        optimisticIdRef.current = null
      }

      // Rollback optimistic mode change on error
      const previousMode = (snapshot as {previousMode?: CLIAgentMode})?.previousMode
      if (previousMode) {
        handleModeChangeError(previousMode)
      }
    },
  })

  const handleSubmit = useCallback(() => {
    if (!value.trim() || isPending) return

    createCommand({
      sessionId,
      content: value.trim(),
    })

    setValue('')
    onCommandSent?.()
  }, [value, isPending, sessionId, createCommand, onCommandSent])

  const handlePromptRespond = useCallback(
    (toolCallId: string, response: unknown, commandType: CommandType) => {
      if (!pendingPrompt || isPending) return

      isPromptResponseRef.current = true
      optimisticIdRef.current = addOptimisticResponse({
        toolCallId,
        response,
        commandType,
      })

      createCommand({
        sessionId,
        content: JSON.stringify(response),
        type: commandType,
      })
    },
    [pendingPrompt, isPending, sessionId, createCommand, addOptimisticResponse],
  )

  const onModeChange = useCallback(
    (mode: CLIAgentMode) => {
      const {previousMode} = handleModeChange(mode)
      createCommand({
        sessionId,
        content: JSON.stringify({mode}),
        type: COMMAND_TYPES.MODE_SWITCH,
        snapshot: {previousMode},
      })
    },
    [sessionId, createCommand, handleModeChange],
  )

  if (!isActiveSession(session)) {
    const resumeCommand = agentTaskId ? `copilot --remote --resume ${agentTaskId}` : 'copilot --remote --resume'
    return (
      <div className={styles.inputWrapper}>
        <BlockedTaskInput>
          Session has ended. Reconnect with <strong>{resumeCommand}</strong>
        </BlockedTaskInput>
      </div>
    )
  }

  if (remoteSteerable === false) {
    return (
      <>
        <CLIChatInputHeader agentTaskId={agentTaskId} connectionStatus={connectionStatus} />
        <div className={styles.inputWrapper}>
          <BlockedTaskInput>
            Steering disabled. Type <strong>/remote</strong> to enable steering. To enable steering for a new session
            start it with <strong>copilot --remote</strong>
          </BlockedTaskInput>
        </div>
      </>
    )
  }

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.skeletonWrapper}>
          <LoadingSkeleton variant="rounded" height="100%" width="100%" />
        </div>
      </div>
    )
  }

  if (pendingPrompt) {
    return (
      <>
        <CLIChatInputHeader
          agentTaskId={agentTaskId}
          connectionStatus={connectionStatus}
          cliRemoteNoticeDismissed={cliRemoteNoticeDismissed}
        />
        <PromptInput
          key={pendingPrompt.toolCallId}
          prompt={pendingPrompt}
          onRespond={handlePromptRespond}
          onDismiss={dismissPrompt}
        />
      </>
    )
  }

  return (
    <>
      <CLIChatInputHeader
        agentTaskId={agentTaskId}
        connectionStatus={connectionStatus}
        cliRemoteNoticeDismissed={cliRemoteNoticeDismissed}
      />
      <ChatInputDisplay
        value={value}
        onChange={setValue}
        onSubmit={handleSubmit}
        placeholder="Send a message to the CLI session..."
        isSubmitting={isPending}
        toolbarSlot={<CLIModePicker selectedMode={agentMode} onModeChange={onModeChange} disabled={isModePending} />}
        stopSlot={<CLIStopButton session={session} />}
      />
    </>
  )
}
